import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Building2,
  CheckCircle2,
  Crosshair,
  Gauge,
  GitBranch,
  LoaderCircle,
  MapPinned,
  Radar,
  RefreshCw,
  Save,
  Search,
  ShieldAlert,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';

interface RelevanceProfile {
  customerId: string;
  sectors: string[];
  regions: string[];
  notes: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface RelevanceComponent {
  key: string;
  label: string;
  weight: number;
  available: boolean;
  score: number;
  contribution: number;
  summary: string;
}

interface RelevanceEvidence {
  kind: 'incident' | 'alert' | 'indicator' | 'technique' | 'cve' | 'targeting';
  id: string;
  title: string;
  detail: string;
  severity?: string | null;
  timeGenerated?: string | null;
  value?: string | null;
}

interface RelevanceActor {
  actorId: string;
  actorName: string;
  mitreId: string;
  nation: string;
  motivations: string[];
  score: number;
  tier: 'Critical' | 'High' | 'Elevated' | 'Moderate' | 'Low';
  evidenceCoverage: number;
  components: RelevanceComponent[];
  metrics: {
    matchedIncidents: number;
    matchedAlerts: number;
    highSeverityMatches: number;
    matchedTechniques: number;
    totalActorTechniques: number;
    matchedIndicators: number;
    highConfidenceIndicators: number;
    sightedIndicators: number;
    iocSightings: number;
    exposedCves: number;
    exposedDevices: number;
    sectorMatches: number;
    regionMatches: number;
  };
  matches: { sectors: string[]; regions: string[]; techniques: string[]; cves: string[] };
  evidence: RelevanceEvidence[];
  recommendations: string[];
  explanation: string;
}

interface RelevanceSource {
  key: string;
  label: string;
  available: boolean;
  count: number;
  message: string;
}

interface RelevanceAssessment {
  runId: string;
  customerId: string;
  lookbackDays: number;
  generatedAt: string;
  scoreModel: { version: string; description: string; weights: Record<string, number> };
  profile: RelevanceProfile;
  summary: {
    assessedActors: number;
    critical: number;
    high: number;
    elevated: number;
    moderate: number;
    low: number;
    evidenceCoverage: number;
    topActorId: string | null;
    topActorName: string | null;
    topScore: number;
  };
  sources: RelevanceSource[];
  actors: RelevanceActor[];
  warnings: string[];
}

interface RelevanceRun {
  id: string;
  customerId: string;
  lookbackDays: number;
  status: string;
  generatedAt: string;
  topActorName: string | null;
  topScore: number;
  critical: number;
  high: number;
  evidenceCoverage: number;
  error: string | null;
}

interface HuntPlanResponse { ok: true; message: string }
interface HuntAskResponse { ok: true; request?: { id?: string; status?: string; externalCampaignId?: string | null } }

type TierFilter = 'All' | RelevanceActor['tier'];

function fmtDate(value?: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function tierClass(tier: RelevanceActor['tier']): string {
  if (tier === 'Critical') return 'border-rose-400/35 bg-rose-500/12 text-rose-200';
  if (tier === 'High') return 'border-orange-400/35 bg-orange-500/12 text-orange-200';
  if (tier === 'Elevated') return 'border-amber-400/35 bg-amber-500/12 text-amber-100';
  if (tier === 'Moderate') return 'border-cyan-400/30 bg-cyan-500/10 text-cyan-200';
  return 'border-slate-500/25 bg-slate-500/10 text-slate-300';
}

function scoreBarClass(score: number): string {
  if (score >= 80) return 'bg-rose-400';
  if (score >= 65) return 'bg-orange-400';
  if (score >= 45) return 'bg-amber-400';
  if (score >= 25) return 'bg-cyan-400';
  return 'bg-slate-500';
}

function evidenceIcon(kind: RelevanceEvidence['kind']) {
  if (kind === 'incident' || kind === 'alert') return ShieldAlert;
  if (kind === 'indicator') return Crosshair;
  if (kind === 'technique') return GitBranch;
  if (kind === 'cve') return AlertTriangle;
  return Target;
}

function csvList(value: string): string[] {
  return Array.from(new Set(value.split(/[;,\n]/g).map((item) => item.trim()).filter(Boolean)));
}

function metricValue(value: number): string { return Number(value || 0).toLocaleString(); }

function PermissionNotice({ text }: { text: string }) {
  return <div className="rounded-2xl border border-amber-400/25 bg-amber-500/10 p-6 text-sm text-amber-100">{text}</div>;
}

export function CustomerThreatRelevanceTab({
  onOpenThreatLandscapeActor,
  onOpenIndicatorLandscape,
}: {
  onOpenThreatLandscapeActor?: (actorId: string) => void;
  onOpenIndicatorLandscape?: () => void;
}) {
  const { customers, activeCustomerId, activeCustomer, setActiveCustomerId } = useCustomers();
  const { canCapability } = useAuth();
  const canView = canCapability('threat_intel', 'view_customer_threat_relevance');
  const canRun = canCapability('threat_intel', 'run_customer_threat_relevance');
  const canManageProfile = canCapability('threat_intel', 'manage_customer_relevance_profile');
  const canEvidence = canCapability('threat_intel', 'view_customer_relevance_evidence');
  const canOpenThreat = canCapability('threat_intel', 'view_threat_landscape');
  const canOpenIndicator = canCapability('threat_intel', 'view_indicator_landscape');
  const canHunt = canCapability('threat_intel', 'hunt_actor_in_customer') && canCapability('hunts', 'run_threat_hunt_agent');

  const [profile, setProfile] = useState<RelevanceProfile | null>(null);
  const [sectorText, setSectorText] = useState('');
  const [regionText, setRegionText] = useState('');
  const [notes, setNotes] = useState('');
  const [assessment, setAssessment] = useState<RelevanceAssessment | null>(null);
  const [history, setHistory] = useState<RelevanceRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [huntBusy, setHuntBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [lookbackDays, setLookbackDays] = useState(30);
  const [tierFilter, setTierFilter] = useState<TierFilter>('All');
  const [search, setSearch] = useState('');
  const [selectedActorId, setSelectedActorId] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [showModel, setShowModel] = useState(false);

  const customerId = activeCustomerId && activeCustomerId !== 'default' ? activeCustomerId : '';

  const load = useCallback(async () => {
    if (!canView || !customerId) return;
    setLoading(true); setError('');
    try {
      const [profileResponse, latestResponse, historyResponse] = await Promise.all([
        apiFetch<{ ok: true; profile: RelevanceProfile }>(`/threat-intel/relevance/profile?customerId=${encodeURIComponent(customerId)}`),
        apiFetch<{ ok: true; assessment: RelevanceAssessment | null }>(`/threat-intel/relevance/latest?customerId=${encodeURIComponent(customerId)}`),
        apiFetch<{ ok: true; runs: RelevanceRun[] }>(`/threat-intel/relevance/history?customerId=${encodeURIComponent(customerId)}&limit=12`),
      ]);
      setProfile(profileResponse.profile);
      setSectorText(profileResponse.profile.sectors.join(', '));
      setRegionText(profileResponse.profile.regions.join(', '));
      setNotes(profileResponse.profile.notes || '');
      setAssessment(latestResponse.assessment);
      setHistory(historyResponse.runs ?? []);
      setSelectedActorId((current) => latestResponse.assessment?.actors.some((actor) => actor.actorId === current) ? current : (latestResponse.assessment?.actors[0]?.actorId ?? ''));
      setLookbackDays(latestResponse.assessment?.lookbackDays ?? 30);
    } catch (e) { setError(e instanceof Error ? e.message : 'Customer Threat Relevance could not load.'); }
    finally { setLoading(false); }
  }, [canView, customerId]);

  useEffect(() => { void load(); }, [load]);

  const filteredActors = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (assessment?.actors ?? []).filter((actor) => {
      if (tierFilter !== 'All' && actor.tier !== tierFilter) return false;
      if (!needle) return true;
      return [actor.actorName, actor.mitreId, actor.nation, ...actor.motivations, ...actor.matches.sectors, ...actor.matches.regions, ...actor.matches.techniques, ...actor.matches.cves].join(' ').toLowerCase().includes(needle);
    });
  }, [assessment, search, tierFilter]);

  const selectedActor = useMemo(() => assessment?.actors.find((actor) => actor.actorId === selectedActorId) ?? assessment?.actors[0] ?? null, [assessment, selectedActorId]);

  async function saveProfile() {
    if (!customerId || !canManageProfile) return;
    setSaveBusy(true); setError(''); setNotice('');
    try {
      const response = await apiFetch<{ ok: true; profile: RelevanceProfile; message?: string }>('/threat-intel/relevance/profile', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, sectors: csvList(sectorText), regions: csvList(regionText), notes }),
      });
      setProfile(response.profile); setSectorText(response.profile.sectors.join(', ')); setRegionText(response.profile.regions.join(', ')); setNotes(response.profile.notes || '');
      setNotice(response.message ?? 'Customer relevance profile saved.');
    } catch (e) { setError(e instanceof Error ? e.message : 'Customer relevance profile could not be saved.'); }
    finally { setSaveBusy(false); }
  }

  async function runAssessment() {
    if (!customerId || !canRun) return;
    setRunBusy(true); setError(''); setNotice('');
    try {
      const response = await apiFetch<{ ok: true; assessment: RelevanceAssessment }>('/threat-intel/relevance/assess', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, lookbackDays }),
      });
      setAssessment(response.assessment);
      setSelectedActorId(response.assessment.actors[0]?.actorId ?? '');
      setNotice(`Customer Threat Relevance completed across ${response.assessment.summary.assessedActors} synchronized actors.`);
      const historyResponse = await apiFetch<{ ok: true; runs: RelevanceRun[] }>(`/threat-intel/relevance/history?customerId=${encodeURIComponent(customerId)}&limit=12`);
      setHistory(historyResponse.runs ?? []);
    } catch (e) { setError(e instanceof Error ? e.message : 'Customer Threat Relevance assessment failed.'); }
    finally { setRunBusy(false); }
  }

  async function launchHunt(actor: RelevanceActor) {
    if (!customerId || !canHunt) return;
    setHuntBusy(true); setError(''); setNotice('');
    try {
      const plan = await apiFetch<HuntPlanResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(actor.actorId)}/hunt-plan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId }),
      });
      const result = await apiFetch<HuntAskResponse>('/hunts/agent/ask', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, message: `${plan.message} Customer Threat Relevance context: score ${actor.score}/100 (${actor.tier}); ${actor.explanation}` }),
      });
      setNotice(`Threat Hunt Agent launched for ${actor.actorName}${result.request?.id ? ` · request ${result.request.id}` : ''}.`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Threat Hunt Agent launch failed.'); }
    finally { setHuntBusy(false); }
  }

  if (!canView) return <PermissionNotice text="Customer Threat Relevance is hidden by Permissions Master (threat_intel.view_customer_threat_relevance)." />;

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-slate-950 via-slate-950 to-slate-900 p-5 shadow-xl">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-cyan-300"><Gauge size={16} /> Phase 4 · Customer Threat Relevance</div>
            <h3 className="mt-2 text-2xl font-black text-white">Translate global threat intelligence into customer-specific priority</h3>
            <p className="mt-2 max-w-5xl text-sm leading-6 text-blue-100/70">KloudSecure ranks synchronized threat actors against explicit customer context and live customer evidence. The score is deterministic and explainable: targeting alignment, Sentinel incident/alert evidence, observed ATT&CK overlap, customer ThreatIntelIndicators context and Microsoft Defender vulnerability exposure.</p>
          </div>
          <div className="min-w-[320px] rounded-xl border border-white/10 bg-white/5 p-3">
            <label className="text-[10px] font-black uppercase tracking-wide text-blue-100/55">Selected customer</label>
            <select value={customerId} onChange={(e) => setActiveCustomerId(e.target.value)} className="mt-2 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm font-bold text-white outline-none focus:border-cyan-400/50">
              <option value="">Select customer…</option>
              {customers.filter((customer) => customer.enabled).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
            </select>
            {activeCustomer && customerId && <div className="mt-2 text-[10px] leading-4 text-blue-100/55">Tenant {activeCustomer.tenantId || '-'} · {activeCustomer.lighthouseMode ? 'Azure Lighthouse' : 'Customer SPN'}<br />Workspace {activeCustomer.workspaceName || activeCustomer.workspaceId || 'Not configured'}</div>}
          </div>
        </div>

        {!customerId && <div className="mt-4 rounded-xl border border-amber-400/25 bg-amber-500/10 px-4 py-3 text-xs text-amber-100"><b>Customer context required.</b> Phase 4 never evaluates or queries a default/MSSP tenant implicitly.</div>}
        {error && <div className="mt-4 rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-xs text-rose-100">{error}</div>}
        {notice && <div className="mt-4 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-100">{notice}</div>}
      </section>

      {customerId && (
        <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-muted-foreground"><Building2 size={14} /> Customer relevance profile</div>
              <div className="mt-1 text-sm font-semibold text-foreground">Targeting context used only for the 20% alignment signal</div>
              <div className="mt-1 text-xs text-muted-foreground">If sectors/regions are empty, Phase 4 excludes that component instead of inventing customer context.</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setShowProfile((value) => !value)} className="rounded-lg border border-border bg-background px-3 py-2 text-xs font-bold text-foreground hover:bg-muted">{showProfile ? 'Hide profile' : 'Edit / review profile'}</button>
              {profile?.updatedAt && <span className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[10px] text-muted-foreground">Updated {fmtDate(profile.updatedAt)}</span>}
            </div>
          </div>
          {showProfile && (
            <div className="mt-4 grid gap-4 xl:grid-cols-2">
              <div>
                <label className="text-xs font-bold text-foreground">Business sectors / industries</label>
                <textarea value={sectorText} onChange={(e) => setSectorText(e.target.value)} disabled={!canManageProfile} rows={3} placeholder="Financial Services, Banking, Government, Energy" className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50 disabled:opacity-60" />
                <div className="mt-1 text-[10px] text-muted-foreground">Comma, semicolon or line separated. Matched against source-backed actor target sectors.</div>
              </div>
              <div>
                <label className="text-xs font-bold text-foreground">Operating / priority regions</label>
                <textarea value={regionText} onChange={(e) => setRegionText(e.target.value)} disabled={!canManageProfile} rows={3} placeholder="South Asia, Southeast Asia, India" className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50 disabled:opacity-60" />
                <div className="mt-1 text-[10px] text-muted-foreground">Only source-backed actor target-region relationships can score this field.</div>
              </div>
              <div className="xl:col-span-2">
                <label className="text-xs font-bold text-foreground">Analyst context notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canManageProfile} rows={2} placeholder="Optional customer context; not directly converted into score points." className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50 disabled:opacity-60" />
              </div>
              <div className="xl:col-span-2 flex justify-end">
                <button type="button" onClick={() => void saveProfile()} disabled={!canManageProfile || saveBusy} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-black text-primary-foreground disabled:opacity-50"><Save size={14} /> {saveBusy ? 'Saving…' : 'Save profile'}</button>
              </div>
              {!canManageProfile && <div className="xl:col-span-2 rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">Your role can view the scoring profile but cannot change it.</div>}
            </div>
          )}
        </section>
      )}

      {customerId && (
        <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-muted-foreground"><Radar size={14} /> Assessment control</div>
              <div className="mt-1 text-sm font-semibold text-foreground">Latest assessment {assessment ? `· ${fmtDate(assessment.generatedAt)}` : '· not yet run'}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={lookbackDays} onChange={(e) => setLookbackDays(Number(e.target.value))} className="rounded-lg border border-border bg-background px-3 py-2 text-xs font-bold text-foreground">
                <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
              </select>
              <button type="button" onClick={() => void load()} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs font-bold text-foreground hover:bg-muted disabled:opacity-50"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh saved</button>
              <button type="button" onClick={() => void runAssessment()} disabled={!canRun || runBusy} className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-4 py-2 text-xs font-black text-slate-950 disabled:opacity-50"><Sparkles size={14} /> {runBusy ? 'Assessing customer…' : 'Run relevance assessment'}</button>
            </div>
          </div>
          {!canRun && <div className="mt-3 rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">Your role can view the latest Phase 4 result but cannot trigger a fresh assessment.</div>}
          {runBusy && <div className="mt-4 flex items-center gap-3 rounded-xl border border-cyan-400/20 bg-cyan-500/5 px-4 py-3 text-xs text-cyan-100"><LoaderCircle size={16} className="animate-spin" /><div><b>Building customer evidence pack…</b><div className="mt-1 text-blue-100/60">Sentinel incidents/alerts, ATT&CK context, ThreatIntelIndicators and tenant-safe MDE vulnerability evidence are evaluated in parallel. This can take a little while on large workspaces.</div></div></div>}
        </section>
      )}

      {assessment && (
        <>
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <MetricCard label="Top relevance" value={`${assessment.summary.topScore}/100`} detail={assessment.summary.topActorName || 'No actor'} icon={TrendingUp} />
            <MetricCard label="Critical" value={metricValue(assessment.summary.critical)} detail="80–100" icon={ShieldAlert} />
            <MetricCard label="High" value={metricValue(assessment.summary.high)} detail="65–79" icon={AlertTriangle} />
            <MetricCard label="Elevated" value={metricValue(assessment.summary.elevated)} detail="45–64" icon={Activity} />
            <MetricCard label="Actors assessed" value={metricValue(assessment.summary.assessedActors)} detail={`${assessment.lookbackDays}d lookback`} icon={Radar} />
            <MetricCard label="Evidence coverage" value={`${assessment.summary.evidenceCoverage}%`} detail="Available weighted signals" icon={CheckCircle2} />
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-xs font-black uppercase tracking-wide text-muted-foreground">Evidence source health</div>
                <div className="mt-1 text-xs text-muted-foreground">Unavailable sources are shown explicitly and reduce score confidence rather than being treated as zero evidence.</div>
              </div>
              <button type="button" onClick={() => setShowModel((value) => !value)} className="rounded-lg border border-border bg-background px-3 py-2 text-xs font-bold text-foreground hover:bg-muted">{showModel ? 'Hide scoring model' : 'Explain scoring model'}</button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {assessment.sources.map((source) => <SourceCard key={source.key} source={source} />)}
            </div>
            {showModel && (
              <div className="mt-4 rounded-xl border border-cyan-400/20 bg-cyan-500/5 p-4 text-xs text-blue-100/75">
                <div className="font-black text-cyan-200">KloudSecure relevance model v{assessment.scoreModel.version}</div>
                <div className="mt-2 leading-5">{assessment.scoreModel.description}</div>
                <div className="mt-3 grid gap-2 md:grid-cols-5">
                  <WeightChip label="Targeting" value={assessment.scoreModel.weights.targeting ?? 0} />
                  <WeightChip label="Incidents / alerts" value={assessment.scoreModel.weights.incident_activity ?? 0} />
                  <WeightChip label="ATT&CK overlap" value={assessment.scoreModel.weights.attack_overlap ?? 0} />
                  <WeightChip label="Customer IoCs" value={assessment.scoreModel.weights.ioc_context ?? 0} />
                  <WeightChip label="MDE exposure" value={assessment.scoreModel.weights.mde_exposure ?? 0} />
                </div>
                <div className="mt-3 text-[10px] text-blue-100/55">The model does not claim attribution. Actor names/aliases matched in customer telemetry are evidence candidates and must be reviewed in context. Missing source categories are removed from the normalized weighting and a coverage factor reduces overconfidence.</div>
              </div>
            )}
            {assessment.warnings.length > 0 && <div className="mt-4 rounded-xl border border-amber-400/20 bg-amber-500/10 p-3 text-xs text-amber-100"><b>Assessment warnings:</b><ul className="mt-2 list-disc space-y-1 pl-5">{assessment.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></div>}
          </section>

          <section className="grid gap-5 2xl:grid-cols-[minmax(0,1.45fr)_minmax(390px,0.8fr)]">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div><div className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-muted-foreground"><BarChart3 size={14} /> Customer threat relevance ranking</div><div className="mt-1 text-sm text-muted-foreground">Ranked against the selected customer - not a global actor severity list.</div></div>
                <div className="flex flex-wrap gap-2">
                  <div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actor, TTP, CVE…" className="w-56 rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-xs text-foreground outline-none focus:border-primary/50" /></div>
                  <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value as TierFilter)} className="rounded-lg border border-border bg-background px-3 py-2 text-xs font-bold text-foreground"><option>All</option><option>Critical</option><option>High</option><option>Elevated</option><option>Moderate</option><option>Low</option></select>
                </div>
              </div>
              <div className="mt-4 overflow-hidden rounded-xl border border-border">
                <div className="grid grid-cols-[minmax(180px,1.4fr)_90px_105px_minmax(220px,1fr)] gap-3 bg-muted/40 px-4 py-2 text-[10px] font-black uppercase tracking-wide text-muted-foreground">
                  <span>Actor</span><span>Score</span><span>Tier</span><span>Evidence signals</span>
                </div>
                <div className="divide-y divide-border">
                  {filteredActors.slice(0, 100).map((actor) => {
                    const selected = selectedActor?.actorId === actor.actorId;
                    return <button key={actor.actorId} type="button" onClick={() => setSelectedActorId(actor.actorId)} className={`grid w-full grid-cols-[minmax(180px,1.4fr)_90px_105px_minmax(220px,1fr)] gap-3 px-4 py-3 text-left transition-colors ${selected ? 'bg-cyan-500/8' : 'hover:bg-muted/30'}`}>
                      <div className="min-w-0"><div className="truncate text-sm font-black text-foreground">{actor.actorName}</div><div className="mt-1 truncate text-[10px] text-muted-foreground">{actor.nation || 'Unknown'} · {actor.mitreId || 'No MITRE ID'}</div></div>
                      <div><div className="text-lg font-black text-foreground">{actor.score}</div><div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div className={`h-full rounded-full ${scoreBarClass(actor.score)}`} style={{ width: `${actor.score}%` }} /></div></div>
                      <div><span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-black ${tierClass(actor.tier)}`}>{actor.tier}</span><div className="mt-1 text-[10px] text-muted-foreground">{actor.evidenceCoverage}% coverage</div></div>
                      <div className="flex flex-wrap gap-1.5 text-[10px]"><SignalPill text={`${actor.metrics.matchedIncidents + actor.metrics.matchedAlerts} incident/alert`} active={actor.metrics.matchedIncidents + actor.metrics.matchedAlerts > 0} /><SignalPill text={`${actor.metrics.matchedTechniques} TTP`} active={actor.metrics.matchedTechniques > 0} /><SignalPill text={`${actor.metrics.matchedIndicators} IoC`} active={actor.metrics.matchedIndicators > 0} /><SignalPill text={`${actor.metrics.exposedCves} CVE`} active={actor.metrics.exposedCves > 0} /></div>
                    </button>;
                  })}
                  {!filteredActors.length && <div className="px-4 py-8 text-center text-sm text-muted-foreground">No actors match the current filters.</div>}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-cyan-400/20 bg-slate-950 p-5 shadow-xl">
              {selectedActor ? <ActorRelevanceDossier actor={selectedActor} canEvidence={canEvidence} canOpenThreat={canOpenThreat} canOpenIndicator={canOpenIndicator} canHunt={canHunt} huntBusy={huntBusy} onOpenThreat={() => onOpenThreatLandscapeActor?.(selectedActor.actorId)} onOpenIndicator={() => onOpenIndicatorLandscape?.()} onHunt={() => void launchHunt(selectedActor)} /> : <div className="text-sm text-blue-100/60">Select an actor from the ranking.</div>}
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-muted-foreground"><TrendingUp size={14} /> Recent relevance runs</div>
            <div className="mt-3 overflow-hidden rounded-xl border border-border">
              <div className="grid grid-cols-[160px_90px_minmax(180px,1fr)_80px_90px_100px] gap-3 bg-muted/40 px-4 py-2 text-[10px] font-black uppercase tracking-wide text-muted-foreground"><span>Generated</span><span>Lookback</span><span>Top actor</span><span>Score</span><span>Critical</span><span>Coverage</span></div>
              <div className="divide-y divide-border">
                {history.map((run) => <div key={run.id} className="grid grid-cols-[160px_90px_minmax(180px,1fr)_80px_90px_100px] gap-3 px-4 py-3 text-xs text-foreground"><span>{fmtDate(run.generatedAt)}</span><span>{run.lookbackDays}d</span><span className="truncate font-bold">{run.topActorName || '-'}</span><span className="font-black">{run.topScore}</span><span>{run.critical}</span><span>{run.evidenceCoverage}%</span></div>)}
                {!history.length && <div className="px-4 py-6 text-center text-sm text-muted-foreground">No saved relevance runs yet.</div>}
              </div>
            </div>
          </section>
        </>
      )}

      {customerId && !assessment && !loading && (
        <section className="rounded-2xl border border-dashed border-cyan-400/25 bg-cyan-500/5 p-8 text-center">
          <Gauge size={30} className="mx-auto text-cyan-300" />
          <h4 className="mt-3 text-lg font-black text-foreground">No saved Customer Threat Relevance assessment yet</h4>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">Configure the customer's sectors/regions if available, then run the first read-only relevance assessment. Global Threat Landscape intelligence remains unchanged; Phase 4 only calculates customer-specific priority.</p>
        </section>
      )}
    </div>
  );
}

function MetricCard({ label, value, detail, icon: Icon }: { label: string; value: string; detail: string; icon: typeof Gauge }) {
  return <div className="rounded-xl border border-border bg-card p-4 shadow-sm"><div className="flex items-center justify-between gap-2"><span className="text-[10px] font-black uppercase tracking-wide text-muted-foreground">{label}</span><Icon size={15} className="text-primary" /></div><div className="mt-2 text-2xl font-black text-foreground">{value}</div><div className="mt-1 text-[10px] text-muted-foreground">{detail}</div></div>;
}

function SourceCard({ source }: { source: RelevanceSource }) {
  return <div className={`rounded-xl border p-3 ${source.available ? 'border-emerald-400/20 bg-emerald-500/5' : 'border-amber-400/20 bg-amber-500/8'}`}><div className="flex items-center justify-between gap-2"><span className="text-xs font-black text-foreground">{source.label}</span><span className={`rounded-full border px-2 py-0.5 text-[9px] font-black uppercase ${source.available ? 'border-emerald-400/30 text-emerald-300' : 'border-amber-400/30 text-amber-200'}`}>{source.available ? 'available' : 'unavailable'}</span></div><div className="mt-1 text-lg font-black text-foreground">{source.count.toLocaleString()}</div><div className="mt-1 text-[10px] leading-4 text-muted-foreground">{source.message}</div></div>;
}

function WeightChip({ label, value }: { label: string; value: number }) {
  return <div className="rounded-lg border border-white/10 bg-slate-950/60 p-3"><div className="text-[10px] uppercase tracking-wide text-blue-100/50">{label}</div><div className="mt-1 text-lg font-black text-white">{value}%</div></div>;
}

function SignalPill({ text, active }: { text: string; active: boolean }) {
  return <span className={`rounded-full border px-2 py-1 ${active ? 'border-cyan-400/25 bg-cyan-500/8 text-cyan-200' : 'border-white/10 text-muted-foreground'}`}>{text}</span>;
}

function ActorRelevanceDossier({
  actor,
  canEvidence,
  canOpenThreat,
  canOpenIndicator,
  canHunt,
  huntBusy,
  onOpenThreat,
  onOpenIndicator,
  onHunt,
}: {
  actor: RelevanceActor;
  canEvidence: boolean;
  canOpenThreat: boolean;
  canOpenIndicator: boolean;
  canHunt: boolean;
  huntBusy: boolean;
  onOpenThreat: () => void;
  onOpenIndicator: () => void;
  onHunt: () => void;
}) {
  return <div>
    <div className="flex items-start justify-between gap-3"><div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-300">Customer relevance dossier</div><h4 className="mt-2 text-xl font-black text-white">{actor.actorName}</h4><div className="mt-1 text-xs text-blue-100/55">{actor.nation || 'Unknown'} · {actor.mitreId || 'No MITRE ID'}</div></div><div className="text-right"><div className="text-4xl font-black text-white">{actor.score}</div><span className={`mt-1 inline-flex rounded-full border px-2 py-1 text-[10px] font-black ${tierClass(actor.tier)}`}>{actor.tier}</span></div></div>
    <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10"><div className={`h-full rounded-full ${scoreBarClass(actor.score)}`} style={{ width: `${actor.score}%` }} /></div>
    <p className="mt-4 text-xs leading-5 text-blue-100/70">{actor.explanation}</p>

    <div className="mt-4 space-y-2">{actor.components.map((component) => <div key={component.key} className="rounded-xl border border-white/10 bg-white/[0.03] p-3"><div className="flex items-center justify-between gap-2"><span className="text-xs font-black text-white">{component.label}</span><span className={`text-xs font-black ${component.available ? 'text-cyan-200' : 'text-slate-500'}`}>{component.available ? `${component.score}/100` : 'N/A'}</span></div><div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-cyan-400" style={{ width: `${component.available ? component.score : 0}%` }} /></div><div className="mt-2 text-[10px] leading-4 text-blue-100/50">{component.summary} · weight {component.weight}%{component.available ? ` · contribution ${component.contribution}` : ''}</div></div>)}</div>

    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3"><MiniMetric label="Incidents" value={actor.metrics.matchedIncidents} /><MiniMetric label="Alerts" value={actor.metrics.matchedAlerts} /><MiniMetric label="ATT&CK" value={actor.metrics.matchedTechniques} /><MiniMetric label="IoCs" value={actor.metrics.matchedIndicators} /><MiniMetric label="IoC hits" value={actor.metrics.iocSightings} /><MiniMetric label="Exposed CVEs" value={actor.metrics.exposedCves} /><MiniMetric label="Devices" value={actor.metrics.exposedDevices} /></div>

    <div className="mt-4 flex flex-wrap gap-2">
      {canOpenThreat && <button type="button" onClick={onOpenThreat} className="inline-flex items-center gap-2 rounded-lg border border-cyan-400/25 bg-cyan-500/10 px-3 py-2 text-xs font-black text-cyan-100"><Radar size={13} /> Threat Landscape</button>}
      {canOpenIndicator && <button type="button" onClick={onOpenIndicator} className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/25 bg-emerald-500/10 px-3 py-2 text-xs font-black text-emerald-100"><MapPinned size={13} /> Indicator Landscape</button>}
      {canHunt && <button type="button" onClick={onHunt} disabled={huntBusy} className="inline-flex items-center gap-2 rounded-lg border border-violet-400/25 bg-violet-500/10 px-3 py-2 text-xs font-black text-violet-100 disabled:opacity-50"><Target size={13} /> {huntBusy ? 'Launching…' : 'Launch Hunt Agent'}</button>}
    </div>

    <div className="mt-5"><div className="text-[10px] font-black uppercase tracking-wide text-blue-100/50">Recommended analyst actions</div><ol className="mt-2 space-y-2">{actor.recommendations.map((recommendation, index) => <li key={`${index}-${recommendation}`} className="flex gap-2 rounded-lg border border-white/10 bg-white/[0.03] p-2 text-xs leading-5 text-blue-100/70"><span className="font-black text-cyan-300">{index + 1}.</span><span>{recommendation}</span></li>)}</ol></div>

    <div className="mt-5"><div className="flex items-center justify-between gap-2"><span className="text-[10px] font-black uppercase tracking-wide text-blue-100/50">Evidence samples</span><span className="text-[10px] text-blue-100/40">{actor.evidence.length} returned</span></div>{canEvidence ? <div className="mt-2 space-y-2">{actor.evidence.slice(0, 14).map((item) => { const Icon = evidenceIcon(item.kind); return <div key={`${item.kind}-${item.id}`} className="rounded-lg border border-white/10 bg-white/[0.025] p-3"><div className="flex items-start gap-2"><Icon size={14} className="mt-0.5 shrink-0 text-cyan-300" /><div className="min-w-0"><div className="truncate text-xs font-black text-white">{item.title}</div><div className="mt-1 text-[10px] leading-4 text-blue-100/55">{item.detail || item.kind}{item.timeGenerated ? ` · ${fmtDate(item.timeGenerated)}` : ''}</div></div></div></div>; })}{!actor.evidence.length && <div className="rounded-lg border border-white/10 p-3 text-xs text-blue-100/50">No evidence samples were retained for this actor in the current assessment.</div>}</div> : <div className="mt-2 rounded-lg border border-amber-400/20 bg-amber-500/10 p-3 text-xs text-amber-100">Detailed evidence is hidden by Permissions Master (view_customer_relevance_evidence).</div>}</div>
  </div>;
}

function MiniMetric({ label, value }: { label: string; value: number }) {
  return <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2"><div className="text-[9px] uppercase tracking-wide text-blue-100/45">{label}</div><div className="mt-1 text-base font-black text-white">{value.toLocaleString()}</div></div>;
}
